/**
 * Filter bar for the startup list.
 *
 * Every control writes to the URL rather than to local state, so a filtered
 * view is linkable — PRD §7 expects "the stalled pilots in Iberia" to be
 * shareable, which is impossible if the filters live in memory.
 *
 * Below 768px the bar becomes a bottom sheet with an applied-count badge
 * (plan §7.3): four inline controls at 375px leave no room for the list they
 * filter.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.2, §7.3
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BANDS } from '../../../components/domain/bands';
import { STAGES } from '../../../components/domain/stages';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { Icon } from '../../../components/ui/Icon';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { filterHandler } from '../../../lib/filter-handler';

export interface StartupFiltersProps {
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClear: () => void;
}

/** The controls, shared by the inline bar and the sheet. */
function FilterControls({
  filters,
  onFilterChange,
}: Omit<StartupFiltersProps, 'onClear'>): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <label className="flex-1">
        <span className="sr-only">{t('startups.filters.search')}</span>
        <Input
          type="search"
          value={filters['q'] ?? ''}
          onChange={(event) => {
            onFilterChange('q', event.target.value);
          }}
          placeholder={t('startups.filters.searchPlaceholder')}
        />
      </label>

      <Select
        value={filters['stage']}
        onValueChange={filterHandler('stage', onFilterChange)}
        options={STAGES.map((stage) => ({ value: stage, label: t(`stage.${stage}`) }))}
        placeholder={t('startups.filters.anyStage')}
        ariaLabel={t('startups.filters.stage')}
        className="sm:w-48"
      />

      <Select
        value={filters['priorityBand']}
        onValueChange={filterHandler('priorityBand', onFilterChange)}
        options={BANDS.map((band) => ({ value: band, label: t(`band.${band}`) }))}
        placeholder={t('startups.filters.anyBand')}
        ariaLabel={t('startups.filters.band')}
        className="sm:w-40"
      />
    </>
  );
}

export function StartupFilters({
  filters,
  onFilterChange,
  onClear,
}: StartupFiltersProps): React.JSX.Element {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const appliedCount = Object.keys(filters).length;

  return (
    <>
      {/* Inline bar, 768px and up. */}
      <div className="hidden items-center gap-2 md:flex">
        <FilterControls filters={filters} onFilterChange={onFilterChange} />
        {appliedCount > 0 ? (
          <Button variant="ghost" size="sm" onClick={onClear}>
            {t('startups.filters.clear')}
          </Button>
        ) : null}
      </div>

      {/* Below 768px: a single button opening a sheet, with the applied count
          on it so the user can see filters are active without opening it. */}
      <div className="md:hidden">
        <Button
          variant="secondary"
          onClick={() => {
            setSheetOpen(true);
          }}
          className="w-full"
        >
          <Icon name="search" />
          {appliedCount === 0
            ? t('actions.filter')
            : t('startups.filters.applied', { count: appliedCount })}
        </Button>

        <Dialog
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={t('actions.filter')}
          footer={
            <>
              <Button variant="ghost" onClick={onClear}>
                {t('startups.filters.clear')}
              </Button>
              <Button
                onClick={() => {
                  setSheetOpen(false);
                }}
              >
                {t('actions.filter')}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <FilterControls filters={filters} onFilterChange={onFilterChange} />
          </div>
        </Dialog>
      </div>
    </>
  );
}
